import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import BrandWordmark from '../layout/BrandWordmark';
import { isBrandName, BRAND_NAME } from '../../lib/brand';

const isDirectVideoUrl = (url) => {
  if (!url) return false;
  const u = url.toLowerCase();
  return (
    u.includes('.mp4') ||
    u.includes('.webm') ||
    u.includes('.mov') ||
    (u.includes('supabase') && !u.includes('youtube') && !u.includes('vimeo'))
  );
};

const HeroVideoSection = ({ config: videoConfig }) => {
  const videoRef = useRef(null);

  const useNativeVideo = useMemo(
    () => (videoConfig?.videoUrl ? isDirectVideoUrl(videoConfig.videoUrl) : false),
    [videoConfig?.videoUrl]
  );

  useEffect(() => {
    if (!videoConfig?.videoUrl || !useNativeVideo) return undefined;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'video';
    link.href = videoConfig.videoUrl;
    document.head.appendChild(link);

    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [videoConfig?.videoUrl, useNativeVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    const play = () => {
      video.play().catch(() => {});
    };

    play();
    video.addEventListener('loadeddata', play);
    video.addEventListener('canplay', play);

    return () => {
      video.removeEventListener('loadeddata', play);
      video.removeEventListener('canplay', play);
    };
  }, [videoConfig?.videoUrl, useNativeVideo]);

  if (!videoConfig?.videoUrl) {
    return null;
  }

  return (
    <section
      className="relative min-h-[500px] h-[70vh] sm:h-[75vh] lg:h-[80vh] lg:min-h-[600px] xl:min-h-[700px] overflow-hidden bg-black"
      aria-label={`${BRAND_NAME} tanıtım videosu`}
    >
      <div className="absolute inset-0 bg-black">
        {useNativeVideo ? (
          <video
            ref={videoRef}
            src={videoConfig.videoUrl}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <ReactPlayer
            url={videoConfig.videoUrl}
            playing
            loop
            muted
            width="100%"
            height="100%"
            playsinline
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              minWidth: '100%',
              minHeight: '100%',
              width: 'auto',
              height: 'auto',
            }}
            config={{
              file: {
                attributes: {
                  style: { objectFit: 'cover' },
                  playsInline: true,
                  crossOrigin: 'anonymous',
                  autoPlay: true,
                  muted: true,
                },
              },
              youtube: { playerVars: { autoplay: 1, mute: 1, loop: 1, playsinline: 1 } },
              vimeo: { playerOptions: { autoplay: true, muted: true } },
            }}
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      </div>

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center lg:justify-start lg:pl-12 xl:pl-24">
        <div className="mx-auto max-w-2xl px-4 text-center lg:mx-0 lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
            className="space-y-4"
          >
            {videoConfig.subtitle && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="inline-block text-sm font-medium uppercase tracking-[0.2em] text-white/90 sm:text-base"
              >
                {videoConfig.subtitle}
              </motion.span>
            )}
            {videoConfig.title ? (
              isBrandName(videoConfig.title) ? (
                <BrandWordmark as="h1" size="hero" className="text-white drop-shadow-lg" />
              ) : (
                <h1 className="text-4xl font-bold leading-tight text-white drop-shadow-lg sm:text-5xl lg:text-6xl xl:text-7xl">
                  {videoConfig.title}
                </h1>
              )
            ) : (
              <BrandWordmark as="h1" size="hero" className="text-white drop-shadow-lg" />
            )}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <Link
                to={videoConfig.ctaLink}
                className="pointer-events-auto inline-block rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
              >
                <Button
                  size="lg"
                  className="rounded-full bg-[#e63946] px-8 py-6 text-base text-white shadow-lg transition-all duration-300 hover:scale-105 hover:bg-[#d62839] hover:shadow-xl hover:shadow-[#e63946]/40 sm:text-lg"
                >
                  {videoConfig.ctaText}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroVideoSection;
